import { useMediaQuery, useTheme } from "@mui/material";
import {
  addDays,
  addMinutes,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  parse,
  parseISO,
  startOfMonth,
} from "date-fns";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DateCalendar,
  DatePicker,
  DesktopTimePicker,
  Divider,
  Grid,
  Loading,
  LocalizationProvider,
  Paper,
  TextField,
  Typography,
} from "../../components";
import { value } from "../../helpers";
import { CadastrosService, notification } from "../../services";
import {
  formatCnpj,
  formatCpf,
  formatDate,
  formatPrice,
  responseErros,
} from "../../utils";
import AgendamentoServicosValidator from "../servicos/agendamento/middlewares/agendamento-servicos.validator";

function AgendamentoServicoPublicoView({ match }) {
  const { id } = match.params;

  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [mensagemErro, setMensagemErro] = useState(null);
  const [dataSelecionada, setDataSelecionada] = useState(new Date());
  const [mesSelecionado, setMesSelecionado] = useState(new Date());
  const [agendamento, setAgendamento] = useState({});
  const [horariosAgendados, setHorariosAgendados] = useState([]);
  const [horariosFuncionamento, setHorariosFuncionamento] = useState([]);
  const [configuracoes, setConfiguracoes] = useState({});
  const [horariosList, setHorariosList] = useState([]);

  const cadastrosService = new CadastrosService();
  const fontSize = isPhone ? "16px" : "20px";

  useEffect(() => {
    buscarAgendamentoServico();
  }, [id]);

  const buscarAgendamentoServico = async () => {
    setLoading(true);
    const hash = location.pathname.replace(
      "/cadastros/agendamento-servico/",
      "",
    );
    const res = await cadastrosService.getAgendamentoServico({
      token: hash,
    });
    if (!res?.isAxiosError) {
      if (res.data?.agendado) {
        setMensagemErro(
          "Este serviço já foi agendado! Em caso de dúvidas, favor entrar em contato conosco.",
        );
      } else {
        const {
          diasSemanaFuncionamento,
          intervaloAgendamentos,
          horariosFuncionamento,
          agendamentos,
          ...data
        } = res.data;

        setAgendamento({
          data: new Date(),
          ...data,
        });
        setConfiguracoes({ diasSemanaFuncionamento, intervaloAgendamentos });
        setHorariosFuncionamento(horariosFuncionamento);
        setHorariosAgendados(agendamentos);

        if (intervaloAgendamentos !== "null") {
          atualizarHorariosDisponiveis(
            new Date(),
            horariosFuncionamento,
            agendamentos,
            intervaloAgendamentos,
            data.tempoServico,
          );
        }
        setMensagemErro(null);
      }
    } else {
      const apiMsg = res?.response?.data?.message || res?.message;
      setMensagemErro(Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg);
      responseErros(res);
    }

    setLoading(false);
  };

  function validarHorarioFuncionamento(time) {
    const intervalosFuncionamento = horariosFuncionamento.find(
      (item) => new Date(agendamento?.data).getDay() === item.diaSemana,
    ).intervalos;

    for (const intervalo of intervalosFuncionamento) {
      const abertura = parse(intervalo.abertura, "HH:mm:ss", new Date()),
        fechamento = parse(intervalo.fechamento, "HH:mm:ss", new Date());

      if (agendamento.tempoServico) {
        const terminoServico = addMinutes(time, agendamento.tempoServico);
        if (
          !(isBefore(time, abertura) || isAfter(terminoServico, fechamento))
        ) {
          return true;
        }
      } else if (!(isBefore(time, abertura) || isAfter(time, fechamento))) {
        return true;
      }
    }

    return false;
  }

  function validarHorarioManual() {
    const time = parse(agendamento.hora, "HH:mm", new Date());

    const validacao = validarHorarioFuncionamento(time);
    if (!validacao) return false;

    const agendamentosPrestador =
      horariosAgendados.find((item) => {
        const dataISO = parseISO(item.dia);
        const data = new Date(dataISO);
        return isSameDay(data, dataSelecionada);
      })?.horarios ?? [];

    for (const horarioPrestador of agendamentosPrestador) {
      const start = parse(horarioPrestador.horario, "HH:mm", new Date()),
        end = addMinutes(start, horarioPrestador.tempoServico ?? 0);

      if (isWithinInterval(time, { start, end })) {
        return false;
      }

      if (agendamento.tempoServico) {
        const terminoServico = addMinutes(time, agendamento.tempoServico);
        if (isBefore(start, terminoServico) && isBefore(time, end)) {
          return false;
        }
      }
    }

    return true;
  }

  const handleSubmit = async () => {
    setLoading(true);

    if (configuracoes.intervaloAgendamentos === "null") {
      const validacao = validarHorarioManual();
      if (!validacao) {
        setLoading(false);
        notification.alertaGenericos("Horário indisponível");
        return;
      }
    }

    const [horas, minutos] = agendamento.hora.split(":").map(Number);
    const periodo = new Date(agendamento.data);
    periodo.setHours(horas, minutos, 0, 0);
    const agendamentoTemp = {
      ...agendamento,
      periodo,
    };
    AgendamentoServicosValidator.validate(agendamentoTemp, {
      abortEarly: false,
    })
      .then(async () => {
        const body = {
          ...agendamentoTemp,
          mensagemLembrete: null,
        };
        const retornoAlerta = await notification.confirmacao(
          "Confirmação",
          `Deseja confirmar o agendamento para ${format(
            formatDate.received(agendamentoTemp.periodo),
            "dd/MM/yyyy 'às' HH:mm?",
          )}`,
        );
        if (retornoAlerta.isConfirmed) {
          const response = await cadastrosService.createAgendamentoServico(
            body,
          );
          if (!response.isAxiosError) {
            const servicoTemp = {
              servicoId: agendamento.servicoId,
              clienteId: agendamento.clienteId,
              chaveAtivo: agendamento.chaveAtivo,
              empresaId: agendamento.empresaId,
            };
            await cadastrosService.updateServicoRecorrente(servicoTemp);
            notification.cadastroSucesso();
            setMensagemErro(
              "Serviço agendado com sucesso! Agradecemos a preferência.",
            );
          } else {
            responseErros(response);
          }
        }
      })
      .catch((err) => {
        if (err.isAxiosError) {
          responseErros(err);
        } else {
          notification.erroValidacao(err.errors[0]);
        }
      })
      .finally(() => setLoading(false));
  };

  function formatarHorariosAgendados(horariosAgendados, intervalo) {
    return horariosAgendados?.reduce((acc, curr) => {
      if (!curr.tempoServico) return [...acc, curr.horario];
      const proporcao = curr.tempoServico / intervalo;
      const horarios = [curr.horario];
      let dataAtual = parse(curr.horario, "HH:mm", new Date());

      for (let i = 1; i < proporcao; i++) {
        dataAtual = addMinutes(dataAtual, intervalo);
        const horarioFormatado = format(dataAtual, "HH:mm");
        horarios.push(horarioFormatado);
      }

      return [...acc, ...horarios];
    }, []);
  }

  function formatarHorariosDisponiveis(
    horariosAgendados,
    horariosFuncionamento,
    dataSelecionada,
    intervalo,
  ) {
    const diaSemana = dataSelecionada.getDay();
    const intervalosFuncionamento = horariosFuncionamento.find(
      (item) => diaSemana === item.diaSemana,
    ).intervalos;

    const horarios = [];
    for (const horario of intervalosFuncionamento) {
      let horaInicial = parse(horario.abertura, "HH:mm:ss", new Date()),
        horaFechamento = parse(horario.fechamento, "HH:mm:ss", new Date());

      while (horaInicial < horaFechamento) {
        const horarioString = format(horaInicial, "HH:mm");
        if (addMinutes(horaInicial, intervalo) <= horaFechamento) {
          if (!horariosAgendados?.includes(horarioString)) {
            horarios.push(horarioString);
          }
        }
        horaInicial = addMinutes(horaInicial, intervalo);
      }
    }
    return horarios;
  }

  function filtrarHorariosDisponiveis(
    horariosAgendados,
    horariosFuncionamento,
    dataSelecionada,
    intervalo,
  ) {
    const horariosOcupados = formatarHorariosAgendados(
      horariosAgendados,
      intervalo,
    );
    const horarios = formatarHorariosDisponiveis(
      horariosOcupados,
      horariosFuncionamento,
      dataSelecionada,
      intervalo,
    );
    if (!horarios.length) {
      notification.alertaGenericos("Não há horários disponíveis para este dia");
    }
    return horarios;
  }

  function validarHorariosDisponiveis(
    tempoServico,
    tempoAgendamento,
    horarios,
    dataReferencia
  ) {
    const resultados = [],
      proporcao = tempoServico / tempoAgendamento;
    for (let i = 0; i < horarios.length; i++) {
      const atual = horarios[i];
      let success = true,
        atualTemp = parse(atual, "HH:mm", new Date());

      if (
        isSameDay(dataReferencia, new Date()) &&
        isBefore(atualTemp, new Date())
      ) {
        success = false;
      }

      for (let j = 1, k = i + 1; j < proporcao; j++, k++) {
        atualTemp = addMinutes(atualTemp, tempoAgendamento);
        const dataFormatada = format(atualTemp, "HH:mm");
        const proximo = horarios[k];
        if (dataFormatada !== proximo) {
          success = false;
          break;
        }
      }

      if (success) resultados.push(atual);
    }

    return resultados;
  }

  const atualizarHorariosDisponiveis = (
    dataReferencia,
    horariosFuncionamento,
    horariosAgendados,
    intervalo,
    tempoServico,
  ) => {
    const horariosOcupados = horariosAgendados.find((item) => {
      const dataISO = parseISO(item.dia);
      const data = new Date(dataISO);
      return isSameDay(data, dataReferencia);
    })?.horarios;

    const horarios = filtrarHorariosDisponiveis(
      horariosOcupados,
      horariosFuncionamento,
      dataReferencia,
      intervalo,
    );
    const resultados = validarHorariosDisponiveis(
      tempoServico,
      intervalo,
      horarios,
      dataReferencia
    );
    if (!resultados.length) {
      notification.alertaGenericos("Não há horários disponíveis neste dia");
      setHorariosList([]);
      return;
    }
    setHorariosList(resultados);
  };

  const handleChangeTextfield = (event) => {
    setAgendamento((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const onChangeDate = (name, date) => {
    if (!date) {
      date = "Invalid Date";
    } else {
      const intervalo = configuracoes.intervaloAgendamentos;
      if (intervalo !== "null") {
        atualizarHorariosDisponiveis(
          date,
          horariosFuncionamento,
          horariosAgendados,
          intervalo,
          agendamento.tempoServico,
        );
      }
    }

    setAgendamento((prev) => ({
      ...prev,
      [name]: date != "Invalid Date" ? formatDate.toSend(date) : "",
      hora: null,
    }));
    setDataSelecionada(date);
  };

  const empresaLabel = (() => {
    const cnpjCpf = agendamento?.cnpjCpf;
    if (!agendamento?.nomeFantasia || !cnpjCpf) return "";
    const cnpjFormatado =
      cnpjCpf?.length > 11 ? formatCnpj(cnpjCpf) : formatCpf(cnpjCpf);
    return `${agendamento.nomeFantasia} - ${cnpjFormatado}`;
  })();

  const disableMesAnterior = () => {
    const mesAtual = startOfMonth(new Date(mesSelecionado));
    const mesLimite = startOfMonth(new Date());
    return mesAtual <= mesLimite;
  };

  const disableMesPosterior = () => {
    const mesAtual = startOfMonth(new Date(mesSelecionado));
    const mesLimite = startOfMonth(addDays(new Date(), 30));
    return mesAtual >= mesLimite;
  };

  const shouldDisableDate = (date) => {
    return (
      configuracoes.diasSemanaFuncionamento &&
      !configuracoes.diasSemanaFuncionamento.includes(date.getDay())
    );
  };

  const renderIntervaloAgendamento = () => {
    const start = agendamento.hora,
      tempoServico = agendamento.tempoServico;
    const dataInicio = parse(start, "HH:mm", new Date());
    const end = addMinutes(dataInicio, tempoServico);
    return `${start} - ${format(end, "HH:mm")}`;
  };

  return (
    <Box
      className="bg-white w-100 d-flex flex-column flex-1 justify-content-around align-items-center"
      sx={{
        minHeight: "100dvh",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          px: 2,
          mt: 5,
          mb: 2,
          maxWidth: "100dvh",
        }}
      >
        <Typography
          color="primary"
          variant="h1"
          className="text-uppercase fw-bolder"
          sx={{
            fontSize: {
              xs: 24,
              md: 40,
            },
            lineHeight: 1.2,
            textAlign: "center",
          }}
        >
          Agendamento de Serviço
        </Typography>
      </Box>
      {mensagemErro ? (
        <Card className="mb-4 rounded-4 p-2" sx={{ maxWidth: "70%" }}>
          <CardContent>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "24px",
                textAlign: "center",
                lineHeight: "1.3",
              }}
              className="mb-4"
            >
              Agendamento Indisponível
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              className="mb-3"
              sx={{ fontSize: "20px", textAlign: "justify", lineHeight: "1.2" }}
            >
              {mensagemErro}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <div className="d-flex flex-column gap-3 mx-4 mt-3 w-100 justify-content-center">
          <div className="mx-2">
            <DateCalendar
              value={dataSelecionada}
              shouldDisableDate={shouldDisableDate}
              onMonthChange={(value) =>
                setMesSelecionado(new Date(value).toISOString())
              }
              onChange={(value) => onChangeDate("data", value)}
              minDate={new Date()}
              maxDate={addDays(new Date(), 30)}
              disabledPrevMonth={disableMesAnterior()}
              disabledNextMonth={disableMesPosterior()}
            />
          </div>
          <Divider sx={{ my: 3, border: "1px solid grey", width: "100%" }} />
          {configuracoes.intervaloAgendamentos !== "null" ? (
            horariosList.length ? (
              <div className="d-flex flex-row flex-wrap justify-content-start gap-3 mx-4">
                {horariosList.map((item, index) => (
                  <Chip
                    key={index}
                    label={item}
                    clickable
                    onClick={() =>
                      setAgendamento((prev) => ({
                        ...prev,
                        hora: item,
                      }))
                    }
                    color={agendamento?.hora === item ? "primary" : "default"}
                    variant={agendamento?.hora === item ? "filled" : "outlined"}
                    sx={{
                      fontSize: { xs: "16px", md: "18px" },
                      padding: "21px",
                      width: "115px",
                      justifyContent: "center",
                      "& .MuiChip-label": {
                        px: 1,
                        whiteSpace: "nowrap",
                        width: "100%",
                        textAlign: "center",
                      },
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: 1,
                      },
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="d-flex justify-content-center">
                <Typography
                  variant="h6"
                  sx={{ fontSize, fontWeight: 600, color: "#a99" }}
                >
                  Sem horários disponíveis
                </Typography>
              </div>
            )
          ) : (
            <Grid container spacing={4} className="px-4">
              <Grid item xs={6}>
                <DatePicker
                  id="data"
                  name="data"
                  label="Data"
                  format="dd/MM/yyyy"
                  disabled
                  value={value.date(dataSelecionada)}
                />
              </Grid>
              <Grid item xs={6}>
                <LocalizationProvider>
                  <DesktopTimePicker
                    sx={{ width: "100%" }}
                    name="hora"
                    label="Hora"
                    disabled={!agendamento?.data}
                    onChange={(time) => {
                      if (!time) return;
                      setAgendamento((prev) => ({
                        ...prev,
                        hora:
                          time != "Invalid Date" ? format(time, "HH:mm") : null,
                      }));
                    }}
                    value={
                      agendamento?.hora
                        ? parse(agendamento.hora, "HH:mm", new Date())
                        : null
                    }
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          )}
          <Divider sx={{ my: 3, border: "1px solid grey", width: "100%" }} />
          <Grid container spacing={2} alignItems="stretch">
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", mb: { xs: 3, md: 0 } }}
            >
              <TextField
                id="observacao"
                name="observacao"
                label="Observação"
                variant="outlined"
                value={agendamento?.observacao ?? ""}
                onChange={handleChangeTextfield}
                InputLabelProps={{
                  sx: {
                    fontSize: "20px",
                    marginTop: "-4px",
                  },
                }}
                InputProps={{
                  sx: {
                    "& .MuiInputBase-input": {
                      fontSize: `${fontSize} !important`,
                    },
                  },
                }}
                fullWidth
                multiline
                rows={3}
                sx={{
                  flex: 1,
                  height: "100%",
                  mx: 3,
                  "& .MuiInputBase-root": {
                    height: "100%",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <Paper
                className="py-4 px-3 mx-4 text-white text-center d-flex flex-column gap-3"
                elevation={4}
                sx={{
                  backgroundColor: "#2196F318",
                  flex: 1,
                  border: "2px solid #2196F344",
                  borderRadius: 4,
                  boxShadow: "none !important",
                }}
              >
                <div className="d-flex flex-row justify-content-between">
                  <Typography variant="h6" sx={{ fontSize, fontWeight: 400 }}>
                    Horário
                  </Typography>
                  {agendamento.hora && (
                    <Typography variant="h6" sx={{ fontSize, fontWeight: 600 }}>
                      {renderIntervaloAgendamento()}
                    </Typography>
                  )}
                </div>
                <div className="d-flex flex-row justify-content-between">
                  <Typography variant="h6" sx={{ fontSize, fontWeight: 600 }}>
                    {agendamento.servicoNome}
                  </Typography>
                  <Typography variant="h6" sx={{ fontSize, fontWeight: 600 }}>
                    {formatPrice(agendamento.precoServico)}
                  </Typography>
                </div>
              </Paper>
            </Grid>
          </Grid>
          <Button
            variant="contained"
            className="fw-bold rounded-3"
            disableElevation
            color="primary"
            onClick={handleSubmit}
            disabled={!agendamento.data || !agendamento.hora}
            sx={{
              width: "fit-content",
              alignSelf: "center",
              fontSize: isPhone ? "15px" : "18px",
              marginTop: { xs: 3, md: 5 },
            }}
          >
            Confirmar Agendamento
          </Button>
        </div>
      )}
      <div className="d-flex flex-column align-items-center w-100">
        <Divider sx={{ my: 3, border: "1px solid grey", width: "100%" }} />
        <Box
          sx={{
            px: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              fontSize: {
                xs: 13,
                md: 18,
              },
              lineHeight: 1.2,
            }}
          >
            {empresaLabel}
          </Typography>
        </Box>
      </div>
      <Loading loading={loading} />
    </Box>
  );
}

export default AgendamentoServicoPublicoView;
